import { useEffect, useState } from 'react';
import { X, Folder, Trash2, PieChart } from '../../icons';
import { Button, FormField, Select } from '../../components/shared';
import {
  getStorageBreakdown, emptyStorageFolder, formatBytes,
  type StorageBreakdown, type StorageBucket, type StorageScope,
} from '../../api/workspace';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { layerAlert, layerConfirm } from '../../components/LayerPopup/LayerPopup';
import { tWorkspace } from '../../lib/workspace-i18n';

/**
 * 용량 관리 (2026-08-14 2차) — 사용자 요구:
 *
 *   "스토리지 사용량에서 용량관리를 누르면 폴더별로 용량을 보여주고 비우기를 누르면
 *    해당하는 폴더의 데이터가 삭제되도록 기능 추가"
 *
 * 좌측 하단의 막대는 "얼마나 찼나" 만 말한다. 정리하려면 **어디가 무거운지**를 알아야 하고,
 * 그건 하위 폴더까지 굴려 봐야 나오는 값이라 목록 API 로는 못 낸다(그쪽은 한 겹만 본다).
 *
 * ⚠ '비우기' 는 **휴지통으로 보낸다**(서버가 soft delete). 한 번의 클릭 뒤에 되돌릴 수 없는
 *   삭제를 두지 않는다 — 확인 문구도 "휴지통에서 되돌릴 수 있다" 를 명시한다.
 */
export function StorageManageModal({ onClose, onChanged }: {
  onClose: () => void;
  /** 비운 뒤 목록·사용량 갱신. */
  onChanged: () => void;
}) {
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  const [data, setData] = useState<StorageBreakdown | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * 보고 있는 범위 (2026-08-14 2차) — 개인 · 팀 · 회사.
   * 팀·회사는 **보기 전용**이다(남의 파일이 섞여 있어 '비우기' 가 숫자와 안 맞는다).
   * 어떤 범위를 고를 수 있는지는 서버가 정한다 — 팀 목록도, 회사 열람 권한도 서버 판정이다.
   */
  const [scope, setScope] = useState<StorageScope>('personal');
  const [teamId, setTeamId] = useState<string | null>(null);
  /** 비우는 중인 묶음 — 그 줄만 잠근다(다른 줄은 계속 볼 수 있어야 한다). */
  const [busyId, setBusyId] = useState<string | null | undefined>(undefined);

  useEscapeClose(onClose, busyId !== undefined);

  const load = async (next: { scope: StorageScope; teamId: string | null } = { scope, teamId }) => {
    setError(null);
    try {
      const res = await getStorageBreakdown({ scope: next.scope, teamId: next.teamId });
      setData(res);
      // 팀을 안 고르고 팀 범위를 열면 서버가 첫 팀을 골라 준다 — 화면도 그것을 따라간다.
      if (res.teamId && res.teamId !== next.teamId) setTeamId(res.teamId);
    } catch (err) {
      setError((err as any)?.response?.data?.message
        ?? (err as Error)?.message ?? tWorkspace('workspace.storage.manage_failed'));
      // 실패한 범위의 옛 목록을 그대로 두면 "권한이 없는데 숫자가 보이는" 화면이 된다.
      setData(null);
    }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  /** 범위 전환 — 상태를 먼저 옮기고 그 값으로 조회한다(setState 는 이 함수 안에서 안 보인다). */
  async function switchScope(nextScope: StorageScope, nextTeam: string | null = null) {
    setScope(nextScope);
    setTeamId(nextTeam);
    setData(null);
    await load({ scope: nextScope, teamId: nextTeam });
  }

  /** 묶음 이름 — 루트/이름 미상은 문구로 대신한다(빈 칸을 그리지 않는다). */
  const labelOf = (b: StorageBucket): string => {
    if (b.id === null) return tWorkspace('workspace.storage.bucket_root');
    return b.name || tWorkspace('workspace.storage.bucket_unknown');
  };

  async function handleEmpty(b: StorageBucket) {
    const ok = await layerConfirm(tWorkspace('workspace.storage.confirm_empty', {
      name: labelOf(b), count: b.fileCount,
    }));
    if (!ok) return;
    setBusyId(b.id);
    try {
      const { deleted } = await emptyStorageFolder(b.id);
      await load();
      onChanged();
      void layerAlert(tWorkspace('workspace.storage.emptied', { count: deleted }));
    } catch (err) {
      void layerAlert((err as any)?.response?.data?.message ?? (err as Error).message);
    } finally {
      setBusyId(undefined);
    }
  }

  const usage = data?.usage ?? null;
  /** 막대 비율 — 총량 대비가 아니라 **가장 큰 묶음 대비**다. 총량이 무제한일 수 있고,
   *  한도가 커서 전부 1px 이 되면 비교라는 목적 자체가 사라진다. */
  const maxSize = Math.max(1, ...(data?.buckets ?? []).map((b) => b.sizeBytes));

  return (
    <div
      className="workspace-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget && busyId === undefined) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={tWorkspace('workspace.storage.manage_heading')}
    >
      <div ref={trapRef} className="workspace-modal ws-storage-modal">
        <header className="workspace-modal-header">
          <h2>{tWorkspace('workspace.storage.manage_heading')}</h2>
          <Button variant="ghost" size="sm" iconOnly
            onClick={onClose} disabled={busyId !== undefined} aria-label={tWorkspace('workspace.modal.aria_close')}
            iconLeft={<X size={18} />}
          />
        </header>

        <div className="workspace-modal-body">
          <div className="ws-storage-total">
            <PieChart size={15} aria-hidden />
            <b className="ws-num">
              {usage ? formatBytes(usage.usedBytes) : '—'}
              {' / '}
              {!usage ? '—' : usage.unlimited
                ? tWorkspace('workspace.storage.unlimited')
                : formatBytes(usage.limitBytes)}
            </b>
          </div>
          {/*
            범위 탭 — 팀이 하나도 없고 관리자도 아니면 개인뿐이라 탭 자체를 안 그린다
            (누를 것이 하나인 탭은 자리만 먹는다).
          */}
          {((data?.teams?.length ?? 0) > 0 || data?.usage.scopeType === 'company' || scope !== 'personal') && (
            <div className="ws-storage-scopes" role="tablist" aria-label={tWorkspace('workspace.storage.scope_aria')}>
              <button
                type="button" role="tab" aria-selected={scope === 'personal'}
                className={`ws-storage-scope${scope === 'personal' ? ' ws-storage-scope--on' : ''}`}
                onClick={() => { void switchScope('personal'); }}
              >
                {tWorkspace('workspace.storage.scope_personal')}
              </button>
              {(data?.teams?.length ?? 0) > 0 && (
                <button
                  type="button" role="tab" aria-selected={scope === 'team'}
                  className={`ws-storage-scope${scope === 'team' ? ' ws-storage-scope--on' : ''}`}
                  onClick={() => { void switchScope('team', teamId); }}
                >
                  {tWorkspace('workspace.storage.scope_team')}
                </button>
              )}
              <button
                type="button" role="tab" aria-selected={scope === 'company'}
                className={`ws-storage-scope${scope === 'company' ? ' ws-storage-scope--on' : ''}`}
                onClick={() => { void switchScope('company'); }}
              >
                {tWorkspace('workspace.storage.scope_company')}
              </button>
            </div>
          )}

          {scope === 'team' && (data?.teams?.length ?? 0) > 0 && (
            <FormField className="workspace-modal-field" htmlFor="ws-storage-team"
              label={tWorkspace('workspace.storage.pick_team')}>
              <Select
                value={teamId ?? data?.teamId ?? null}
                options={(data?.teams ?? []).map((t) => ({ value: t.id, label: t.name }))}
                onChange={(v) => { void switchScope('team', v); }}
                portal
              />
            </FormField>
          )}

          <p className="ws-detail-note">
            {tWorkspace('workspace.storage.manage_hint')}
            {/* 왜 비우기가 없는지 알려 주는 유일한 자리 — 감추기만 하면 고장으로 읽힌다. */}
            {data && data.canEmpty === false && (
              <> {tWorkspace('workspace.storage.readonly_hint')}</>
            )}
          </p>

          {error && <div className="workspace-modal-error" role="alert">{error}</div>}
          {data?.truncated && (
            <div className="workspace-modal-error" role="status">
              {tWorkspace('workspace.storage.truncated')}
            </div>
          )}

          {data && data.buckets.length === 0 && (
            <p className="ws-detail-note">{tWorkspace('workspace.storage.manage_empty')}</p>
          )}

          <ul className="ws-storage-buckets">
            {(data?.buckets ?? []).map((b) => (
              <li key={b.id ?? 'root'} className="ws-storage-bucket">
                <span className="ws-storage-bucket-icon" aria-hidden>
                  <Folder size={15} />
                </span>
                <span className="ws-storage-bucket-txt">
                  <b title={labelOf(b)}>{labelOf(b)}</b>
                  <span className="ws-num">
                    {formatBytes(b.sizeBytes)}
                    {' · '}
                    {tWorkspace('workspace.storage.bucket_files', { count: b.fileCount })}
                  </span>
                  <span className="ws-storage-bucket-bar">
                    <span style={{ width: `${Math.round((b.sizeBytes / maxSize) * 100)}%` }} />
                  </span>
                </span>
                {/* 팀·회사는 보기 전용 — 서버가 canEmpty=false 를 준다(옛 응답은 개인뿐이라 기본 true). */}
                {(data?.canEmpty ?? true) && (
                  <button
                    type="button"
                    className="ws-storage-bucket-empty"
                    disabled={busyId !== undefined || b.fileCount === 0}
                    onClick={() => void handleEmpty(b)}
                  >
                    <Trash2 size={13} aria-hidden />
                    {tWorkspace('workspace.storage.empty_btn')}
                  </button>
                )}
              </li>
            ))}
          </ul>

          <footer className="workspace-modal-footer">
            <Button variant="secondary"
              onClick={onClose} disabled={busyId !== undefined}
            >
              {tWorkspace('workspace.modal.btn_cancel')}
            </Button>
          </footer>
        </div>
      </div>
    </div>
  );
}
